import { useNavigate, useLocation } from 'react-router-dom'
import { CheckCircle } from 'lucide-react'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { useTranslation } from '@/i18n'
import { apiService } from '@/service/api'
import WizardLayout from './WizardLayout'

export default function WizardSuccessPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const coworkerName = location.state?.coworkerName as string | undefined
  const coworkerAvatarUrl = location.state?.coworkerAvatarUrl as string | undefined
  const mode = location.state?.mode as 'self' | 'colleague' | undefined

  const isColleague = mode === 'colleague'
  const displayName = coworkerName
    ? `Digital ${coworkerName}`
    : 'Digital Coworker'

  const getInitials = (name: string) =>
    name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()

  const title = isColleague
    ? t('digital_coworker:success.colleague_title', { name: displayName, defaultValue: `Invitation sent for ${displayName}` })
    : displayName

  const subtitle = isColleague
    ? t('digital_coworker:success.colleague_subtitle', 'They will receive an email to connect their account, upload documents, and complete their interviews.')
    : t('digital_coworker:success.subtitle', "You're almost ready. Here's what's next")

  const checklistItems = isColleague
    ? [
        t('digital_coworker:success.profile_created', 'Profile Created'),
        t('digital_coworker:success.colleague_data_setup', 'Data and interview questions set up'),
      ]
    : [
        t('digital_coworker:success.profile_created', 'Profile Created'),
        t('digital_coworker:success.first_interview', '1st Interview'),
      ]

  return (
    <WizardLayout step={isColleague ? 2 : 4} totalSteps={isColleague ? 2 : 4}>
      <div className="flex flex-col items-center text-center pt-16">
        {/* Avatar */}
        <div className="size-16 rounded-full bg-gray-100 dark:bg-dark-accent flex items-center justify-center overflow-hidden mb-4">
          {coworkerAvatarUrl ? (
            <img
              src={apiService.getUrl(coworkerAvatarUrl)}
              alt={displayName}
              className="size-full object-cover"
            />
          ) : (
            <span className="text-xl font-semibold text-orange-600">
              {getInitials(coworkerName || 'DC')}
            </span>
          )}
        </div>

        {/* Title */}
        <h2 className="text-base font-medium text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-[400px]">
          {subtitle}
        </p>

        {/* Checklist */}
        <div className="w-full max-w-[320px] mt-8 space-y-0">
          {checklistItems.map((item) => (
            <div key={item} className="flex items-center gap-3 border-b border-[#e5e5e5] dark:border-dark-border py-3">
              <CheckCircle className="size-5 text-green-500 shrink-0" />
              <span className="text-sm text-muted-foreground">{item}</span>
            </div>
          ))}
        </div>

        {/* Back button */}
        <IntuiButton
          variant="outline"
          className="mt-8"
          onClick={() => navigate('/digital-coworker')}
        >
          {t('digital_coworker:success.back_button', 'Back to Digital Coworker')}
        </IntuiButton>
      </div>
    </WizardLayout>
  )
}
